<template>
  <div class="bg-white w-full overflow-scroll scroll-styling">
    <div class="max-w-7xl mx-auto px-6 py-8">
      <div class="flex justify-between items-center mb-6 gap-4">
        <h1 class="text-2xl md:text-4xl font-bold text-gray-900">Эксперты</h1>
        
        <button
          v-if="isAuthenticated && (user.role && user.role.key !== 1)"
          @click="showCreateForm = true"
          class="px-4 md:px-6 py-2 bg-gray-800 text-white rounded-xl hover:bg-gray-700 transition font-medium text-xs md:text-sm"
        >
          <b>Создать объявление</b>
        </button>
      </div>

      <div class="flex justify-center mb-8 px-4">
        <div class="flex flex-col md:flex-row gap-3 md:gap-0 md:rounded-full md:border-2 md:border-gray-300">
          <button
            @click="activeTab = 'requests'"
            :class="[
              'px-4 md:px-24 py-3 rounded-md md:rounded-l-full font-bold transition-all border-none focus:outline-none text-xs md:text-base',
              activeTab === 'requests'
                ? 'bg-gray-800 text-white'
                : 'text-gray-700 bg-white hover:bg-gray-300'
            ]"
          >
            Запрос на услугу (для работодателей)
          </button>
          <button
            @click="activeTab = 'offers'"
            :class="[
              'px-4 md:px-24 py-3 rounded-md md:rounded-r-full font-bold transition-all border-none focus:outline-none text-xs md:text-base',
              activeTab === 'offers'
                ? 'bg-gray-800 text-white'
                : 'text-gray-700 hover:bg-gray-300'
            ]"
          >
            Предложение услуги (для пилотов)
          </button>
        </div>
      </div>

      <div class="mb-8 flex justify-center px-4">
        <input
          v-model="filters.search"
          type="text"
          placeholder="Поиск по названию или описанию объявления"
          class="w-full md:w-1/2 px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-gray-500 text-xs md:text-sm"
        />
      </div>

      <div v-if="loading" class="text-center py-16">
        <i class="fas fa-spinner fa-spin text-3xl text-gray-400"></i>
      </div>

      <div v-else-if="announcements.length === 0" class="text-center py-12">
        <p class="text-gray-500 text-lg">Объявлений не найдено</p>
      </div>

      <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AnnouncementCard
          v-for="announcement in announcements"
          :key="announcement.id"
          :announcement="announcement"
          @refresh="fetchAnnouncements"
        />
      </div>

      <div v-if="totalPages > 1" class="mt-8 flex justify-center items-center gap-2 md:gap-4 px-4 flex-wrap">
        <button
          @click="currentPage = Math.max(1, currentPage - 1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 text-xs md:text-sm"
        >
          Предыдущая
        </button>
        <span class="text-xs md:text-sm text-gray-600 whitespace-nowrap">Страница {{ currentPage }} из {{ totalPages }}</span>
        <button
          @click="currentPage = Math.min(totalPages, currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 text-xs md:text-sm"
        >
          Следующая
        </button>
      </div>

      <!-- Modal: Create Announcement -->
      <div v-if="showCreateForm" class="fixed inset-0 bg-black bg-opacity-50 z-[10010] flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div class="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
            <h2 class="text-2xl font-bold text-gray-900">Создать объявление</h2>
            <button
              @click="showCreateForm = false"
              class="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ✕
            </button>
          </div>

          <div class="p-6">
            <AnnouncementCreateForm
              @submit="handleCreateAnnouncement"
              @close="showCreateForm = false"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import AnnouncementCard from '@/components/ExpertSearch/AnnouncementCard.vue';
import AnnouncementCreateForm from '@/components/ExpertSearch/AnnouncementCreateForm.vue';
import axios from 'axios';
import { API_PILOT_ANNOUNCEMENTS_PUBLIC, HEADERS_TOKEN_JSON, DEFAULT_PER_PAGE } from '@/const/uri';
import { mapState } from 'vuex';

export default {
  name: 'ExpertSearch',
  components: {
    AnnouncementCard,
    AnnouncementCreateForm,
  },
  computed: {
    ...mapState(["user"]),
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();

    const activeTab = ref('requests');
    const announcements = ref([]);
    const loading = ref(false);
    const currentPage = ref(1);
    const totalPages = ref(1);
    const perPage = DEFAULT_PER_PAGE;
    const isAuthenticated = ref(false);
    const showCreateForm = ref(false);

    const filters = ref({
      search: '',
      pilotStatus: '',
      category: '',
      requiredCategory: '',
    });

    const fetchAnnouncements = async () => {
      loading.value = true;
      try {
        const params = {
          p: currentPage.value,
          page_size: perPage,
          announcement_type: activeTab.value === 'requests' ? 'request' : 'offer',
          status: 'approved',
        };

        // Add search parameter if search query exists
        if (filters.value.search) {
          params.search = filters.value.search;
        }

        if (activeTab.value === 'requests' && filters.value.requiredCategory) {
          params.required_pilot_category = filters.value.requiredCategory;
        }

        // Use appropriate headers based on auth status
        const headers = isAuthenticated.value
          ? HEADERS_TOKEN_JSON()
          : { 'Accept': 'application/json' };

        const response = await axios.get(`${API_PILOT_ANNOUNCEMENTS_PUBLIC.list}`, {
          params,
          headers,
        });

        if (response.data && response.data.data) {
          announcements.value = response.data.data.data.results || [];
          const count = response.data.data.data.count || 0;
          totalPages.value = Math.ceil(count / perPage) || 1;
        }
      } catch (error) {
        console.error('Error fetching announcements:', error);
      } finally {
        loading.value = false;
      }
    };

    const checkAuthentication = () => {
      const user = store.state.user;
      isAuthenticated.value = user && user.role && user.role.key !== -1;
      console.log('isAuthenticated.value:', isAuthenticated.value, user,);
    };

    const handleCreateAnnouncement = async (announcementData) => {
      try {
        const headers = isAuthenticated.value
          ? HEADERS_TOKEN_JSON()
          : { 'Accept': 'application/json' };

        // Use public API endpoint for form submissions
        await axios.post(
          `${API_PILOT_ANNOUNCEMENTS_PUBLIC.create}`,
          announcementData,
          { headers }
        );
        showCreateForm.value = false;
        await fetchAnnouncements();
        alert('После одобрения модератором объявление появится на платформе в течение 3 рабочих дней.');
      } catch (error) {
        console.error('Error creating announcement:', error);
        alert('Ошибка при создании объявления. Пожалуйста, попробуйте снова.');
      }
    };

    watch(activeTab, () => {
      currentPage.value = 1;
      // Update URL with new tab
      router.push({
        query: {
          ...route.query,
          type: activeTab.value
        }
      });
      fetchAnnouncements();
    });

    watch(currentPage, () => {
      // Update URL with new page number
      router.push({
        query: {
          ...route.query,
          page: currentPage.value > 1 ? currentPage.value : undefined
        }
      });
      fetchAnnouncements();
    });

    watch(() => filters.value.search, () => {
      currentPage.value = 1;
      // Update URL with search query
      router.push({
        query: {
          ...route.query,
          search: filters.value.search || undefined,
          page: undefined
        }
      });
      fetchAnnouncements();
    });

    // Handle browser back/forward navigation
    watch(() => route.query, (newQuery) => {
      if (newQuery.type && ['requests', 'offers'].includes(newQuery.type) && activeTab.value !== newQuery.type) {
        activeTab.value = newQuery.type;
      }

      if (newQuery.page && currentPage.value !== parseInt(newQuery.page, 10)) {
        currentPage.value = parseInt(newQuery.page, 10);
      }

      if (newQuery.search && filters.value.search !== newQuery.search) {
        filters.value.search = newQuery.search;
      }
    }, { deep: true });

    onMounted(() => {
      checkAuthentication();

      // Initialize state from URL query parameters
      if (route.query.type && ['requests', 'offers'].includes(route.query.type)) {
        activeTab.value = route.query.type;
      }

      if (route.query.page) {
        currentPage.value = parseInt(route.query.page, 10) || 1;
      }

      if (route.query.search) {
        filters.value.search = route.query.search;
      }

      fetchAnnouncements();
    });

    return {
      activeTab,
      announcements,
      loading,
      currentPage,
      totalPages,
      isAuthenticated,
      showCreateForm,
      filters,
      fetchAnnouncements,
      handleCreateAnnouncement,
    };
  },
};
</script>

<style scoped>
</style>
